"use client";

import type { I18n } from "@lingui/core";
import type { DefaultChannelGroup } from "@/app/[lang]/[workspaceId]/ua/constants";
import {
  DefaultChannelGroupLabels,
  DefaultChannelGroupValues,
} from "@/app/[lang]/[workspaceId]/ua/constants";
import type { VizDatasetFieldMeta } from "./dataset-fields";

function isWhitespace(value: string): boolean {
  return value === " " || value === "\t" || value === "\n" || value === "\r";
}

function normalizeWhitespaceToUnderscore(value: string): string {
  let result = "";
  let inWhitespace = false;
  for (const char of value.trim()) {
    if (isWhitespace(char)) {
      if (!inWhitespace) {
        result += "_";
        inWhitespace = true;
      }
      continue;
    }
    inWhitespace = false;
    result += char;
  }
  return result;
}

function normalizeEnumToken(value: string): string {
  return normalizeWhitespaceToUnderscore(value).toUpperCase();
}

function resolveDefaultChannelGroup(value: string): DefaultChannelGroup | null {
  const normalized = normalizeEnumToken(value);
  const matched = DefaultChannelGroupValues.find(
    (candidate) => candidate === normalized,
  );
  return matched ?? null;
}

/**
 * 空文字/空白文字を除外し、表示に使える最初のラベルを返す。
 */
export function resolveDisplayLabel(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (typeof candidate === "string") {
      const normalized = candidate.trim();
      if (normalized.length > 0) return normalized;
      continue;
    }
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return String(candidate);
    }
  }
  return "";
}

export function resolveCategoryLabel(args: {
  value: unknown;
  fieldMeta?: VizDatasetFieldMeta;
  i18n: Pick<I18n, "_">;
}): string {
  const { value, fieldMeta, i18n } = args;
  if (value === null || value === undefined) return "";

  const raw = String(value);
  const domain = fieldMeta?.labelDomain;

  if (domain === "ua.defaultChannelGroup") {
    const enumValue = resolveDefaultChannelGroup(raw);
    if (enumValue) {
      return i18n._(DefaultChannelGroupLabels[enumValue]);
    }
  }

  return raw;
}
